import { useState } from 'react';
import * as dm from '../../../../../../data-models';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { PageChangeEvent } from '@progress/kendo-react-data-tools';
import PagerSection from '../../../../pager-section';
import {
  MultipleCardContainer,
  StatusFlagModified,
} from '../../../components/tw-components';
import {
  filterRequired,
  nonRequiredTasks,
  filterAwaitingActionTasks,
  filterUpcomingTasks,
  filterOtherTasks,
} from '../../../../../tasks/utils';
import infoCircleOutlineBlack from '../../../../../../images/info-circle-outline-black.svg';

type Props = {
  EETasks: dm.Task[];
  headerLinkName?: string;
  headerLink?: string;
};

export const AssignedTasks = ({
  EETasks,
  headerLinkName,
  headerLink,
}: Props) => {
  const [page, setPage] = useState({
    skip: 0,
    take: 4,
  });

  const handlePageChange = (e: PageChangeEvent) => {
    setPage({
      skip: e.skip,
      take: e.take,
    });
  };

  const requiredTasks = filterRequired(EETasks);
  const recommendedOptional = nonRequiredTasks(EETasks);
  const sortedTasks = [
    ...filterAwaitingActionTasks(requiredTasks),
    ...filterUpcomingTasks(requiredTasks),
    ...filterOtherTasks(recommendedOptional),
  ];

  const { skip, take } = page;
  const visibleTasks = sortedTasks.slice(skip, skip + take);

  const getTaskDetailLink = (task: dm.Task) => ({
    pathname: `/app/task/${task.taskId}/details/${task.taskCodeName}`,
    state: { EETasks },
  });

  const getCategoryChipStyles = (taskCategory: string = '') => {
    const categoryStyles = {
      Required: {
        className: 'tw-bg-unm-secondary04 tw-text-unm-statusflag-text',
        text: 'Action Required',
      },
      Recommended: {
        className:
          'tw-bg-unm-statusflag-optional-bg tw-text-unm-statusflag-text',
        text: 'Optional',
      },
      Optional: {
        className:
          'tw-bg-unm-statusflag-optional-bg tw-text-unm-statusflag-text',
        text: 'Optional',
      },
    } as const;

    return (
      categoryStyles[taskCategory as keyof typeof categoryStyles] ??
      categoryStyles.Required
    );
  };

  return (
    <MultipleCardContainer id="tasks-assigned-card">
      {EETasks && EETasks?.length > 0 ? (
        <div className="tw-flex tw-flex-col tw-h-full">
          {visibleTasks.map((task, index) => {
            const chip = getCategoryChipStyles(task.taskCategory);

            return (
              <div
                className={`tw-px-4 tw-py-5 ${
                  index % 2 === 0 ? 'tw-bg-unm-bg-light' : 'tw-bg-white'
                }`}
                key={task.taskId}
              >
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-mb-3">
                  <div className="tw-min-w-0">
                    <div className="tw-font-semibold tw-text-base tw-leading-5 tw-text-unm-adhoc2 tw-line-clamp-1">
                      {task.taskTitle}
                    </div>
                    <div className="tw-text-md tw-leading-5 tw-text-unm-adhoc2 tw-mt-1">
                      Due {dayjs(task.taskDueDate).format('MM/DD/YYYY')}
                    </div>
                  </div>
                  <Link
                    to={getTaskDetailLink(task)}
                    className="tw-text-base tw-leading-5 tw-text-unm-interactive01-400 tw-no-underline tw-whitespace-nowrap hover:tw-underline"
                  >
                    View Task
                  </Link>
                </div>

                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <div className="tw-text-base tw-leading-6 tw-uppercase tw-tracking-[0.01em] tw-text-[#525252]">
                    {task.taskCategory}
                  </div>
                  {!!task.taskCategory && (
                    <StatusFlagModified
                      data-testid={`claim-${task.taskId}-task-awaiting-action-flag`}
                      className={`tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-whitespace-nowrap ${chip.className}`}
                    >
                      <img
                        src={infoCircleOutlineBlack}
                        alt=""
                        aria-hidden="true"
                        className="tw-w-4 tw-h-4 tw-mr-1"
                      />
                      <span>{chip.text}</span>
                    </StatusFlagModified>
                  )}
                </div>
              </div>
            );
          })}

          <div className="tw-mt-auto tw-px-4 tw-py-3 tw-border-t tw-border-solid tw-border-0 tw-border-unm-border1 tw-bg-white tw-flex tw-items-center tw-gap-0">
            <div className="tw-flex-1 tw-flex tw-justify-center">
              <PagerSection
                skip={skip}
                take={take}
                handlePageChange={handlePageChange}
                elements={sortedTasks}
                className="tw-pb-0 tw-pt-0"
                paddingTop="0"
                paddingBottom="0"
              />
            </div>
            <Link
              to={`${headerLink}`}
              className="tw-text-md tw-font-normal tw-text-unm-interactive01-400 tw-no-underline hover:tw-underline tw-whitespace-nowrap"
            >
              {headerLinkName}
            </Link>
          </div>
        </div>
      ) : (
        <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-unm-primary02-400 tw-text-md tw-mb-4 tw-p-4 tw-bg-unm-bg-light">
          You currently have no assigned tasks.
        </div>
      )}
    </MultipleCardContainer>
  );
};
